<template>
    <div class="content-picker">
        <div class="content-picker-header">
            <div class="content-picker-type-selector">
                <select 
                    v-model="selectedContentType" 
                    class="content-picker-type-select"
                    :disabled="disabled"
                    @change="onContentTypeChange"
                >
                    <option 
                        v-for="type in allowedTypes" 
                        :key="type.value" 
                        :value="type.value"
                    >
                        {{ type.label }}
                    </option>
                </select>
            </div>
        </div>
        <div class="content-picker-body">
            <DealPicker 
                v-if="selectedContentType === 'deal'"
                :model-value="dealId"
                :disabled="disabled"
                @update:model-value="onDealIdUpdate"
                @deal-selected="onDealSelected"
            />
            <ListPicker 
                v-else-if="selectedContentType === 'list'"
                :model-value="articleId"
                :disabled="disabled"
                @update:model-value="onArticleIdUpdate"
                @article-selected="onArticleSelected"
            />
            <GiftListPicker 
                v-else-if="selectedContentType === 'giftList'"
                :model-value="articleId"
                :disabled="disabled"
                @update:model-value="onArticleIdUpdate"
                @article-selected="onArticleSelected"
            />
            <OutletItemPicker 
                v-else-if="selectedContentType === 'outletItem'"
                :model-value="outletItemId"
                :disabled="disabled"
                @update:model-value="onOutletItemIdUpdate"
                @outlet-item-selected="onOutletItemSelected"
            />
        </div>
    </div>
</template>

<script lang="ts">
import { defineComponent, ref, computed, watch } from 'vue'
import DealPicker from './DealPicker.vue'
import ListPicker from './ListPicker.vue'
import GiftListPicker from './GiftListPicker.vue'
import OutletItemPicker from './OutletItemPicker.vue'
import { DealDetails } from '@/classes/DealDetails'
import { Article } from '@/classes/Articles'
import { OutletItem } from '@/classes/OutletItem'

export type ContentType = 'deal' | 'list' | 'giftList' | 'outletItem'

interface ContentTypeOption {
    value: ContentType;
    label: string;
}

export default defineComponent({
    name: 'ContentPicker',
    components: {
        DealPicker,
        ListPicker,
        GiftListPicker,
        OutletItemPicker
    },
    props: {
        allowedContentTypes: {
            type: Array as () => ContentType[],
            default: () => ['deal', 'list', 'giftList', 'outletItem']
        },
        defaultContentType: {
            type: String as () => ContentType,
            default: 'deal'
        },
        modelValue: {
            type: [Number, String, Object],
            default: null
        } as any,
        disabled: {
            type: Boolean,
            default: false
        }
    },
    emits: ['update:modelValue', 'contentSelected'],
    setup(props, { emit }) {
        const selectedContentType = ref<ContentType>(props.defaultContentType)
        const dealId = ref<number | null>(null)
        const articleId = ref<number | null>(null)
        const outletItemId = ref<string | null>(null)

        const contentTypeOptions: ContentTypeOption[] = [
            { value: 'deal', label: 'Deals' },
            { value: 'list', label: 'List' },
            { value: 'giftList', label: 'Gift List' },
            { value: 'outletItem', label: 'Outlet Item' }
        ]

        const allowedTypes = computed(() => {
            return contentTypeOptions.filter(opt => props.allowedContentTypes.includes(opt.value))
        })

        // Initialize selectedContentType to first allowed type if default is not allowed
        if (!props.allowedContentTypes.includes(props.defaultContentType) && allowedTypes.value.length > 0) {
            selectedContentType.value = allowedTypes.value[0].value
        }

        const onContentTypeChange = () => {
            // Clear current selection when switching types
            dealId.value = null
            articleId.value = null
            outletItemId.value = null
            emit('update:modelValue', null)
        }

        const onDealIdUpdate = (value: number | null) => {
            dealId.value = value
        }

        const onDealSelected = (deal: DealDetails) => {
            emit('update:modelValue', deal)
            emit('contentSelected', deal)
        }

        const onArticleIdUpdate = (value: number | null) => {
            articleId.value = value
        }

        const onArticleSelected = (article: Article) => {
            emit('update:modelValue', article)
            emit('contentSelected', article)
        }

        const onOutletItemIdUpdate = (value: string | null) => {
            outletItemId.value = value
        }

        const onOutletItemSelected = (item: OutletItem) => {
            emit('update:modelValue', item)
            emit('contentSelected', item)
        }

        // Watch for external modelValue changes
        watch(() => props.modelValue, (newValue) => {
            if (!newValue) {
                dealId.value = null
                articleId.value = null
                outletItemId.value = null
                return
            }

            if (newValue instanceof DealDetails || (typeof newValue === 'object' && 'dealId' in newValue)) {
                selectedContentType.value = 'deal'
                dealId.value = (newValue as DealDetails).dealId
            } else if (newValue instanceof Article || (typeof newValue === 'object' && 'articleId' in newValue)) {
                const article = newValue as Article
                selectedContentType.value = article.isGiftList ? 'giftList' : 'list'
                articleId.value = article.articleId
            } else if (newValue instanceof OutletItem || (typeof newValue === 'object' && newValue !== null && 'id' in newValue && typeof (newValue as any).id === 'string')) {
                selectedContentType.value = 'outletItem'
                outletItemId.value = (newValue as OutletItem).id
            }
        }, { immediate: true })

        return {
            selectedContentType,
            dealId,
            articleId,
            outletItemId,
            allowedTypes,
            onContentTypeChange,
            onDealIdUpdate,
            onDealSelected,
            onArticleIdUpdate,
            onArticleSelected,
            onOutletItemIdUpdate,
            onOutletItemSelected
        }
    }
})
</script>

<style scoped>
.content-picker {
    display: flex;
    flex-direction: column;
    gap: 15px;
}

.content-picker-header {
    display: flex;
    align-items: center;
    gap: 15px;
    width: 100%;
}

.content-picker-type-selector {
    flex: 1;
}

.content-picker-type-select {
    width: 100%;
    padding: 8px 12px;
    border: 1px solid #ddd;
    border-radius: 4px;
    font-size: 14px;
    box-sizing: border-box;
    background: white;
    cursor: pointer;
    font-weight: 500;
}

.content-picker-type-select:focus {
    outline: none;
    border-color: #2196F3;
    box-shadow: 0 0 0 2px rgba(33, 150, 243, 0.1);
}

.content-picker-type-select:disabled {
    opacity: 0.5;
    cursor: not-allowed;
}

.content-picker-body {
    width: 100%;
}
</style>

